/** Pure helpers for การแจกแชท (settings page, shift grid, team strip, system lines). No Vue imports. */
import type { LeaveRow, RoutingValues, ShiftRow } from '../api/routing/types'
import type { SystemEvent } from '../api/types'

export const DAY_MIN = 1440
const BKK_OFFSET_MS = 7 * 3600 * 1000
const MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
export const WEEKDAYS_SHORT = ['จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.', 'อา.']
export const WEEKDAYS_LONG = ['จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์', 'อาทิตย์']

/** Deep plain copy (drops Vue proxies — structuredClone refuses them). */
export function plain<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T
}

/**
 * Names for tight places (team strip, per-person cells, shift grid): the first word of the POS name
 * ("พลอย ใจดี" → "พลอย"); people who share a first word keep their full name. Full names stay in tooltips.
 */
export function shortNames(people: { id: string; name: string }[]): Map<string, string> {
  const first = (n: string) => n.trim().split(/\s+/)[0] || n
  const count = new Map<string, number>()
  for (const p of people) count.set(first(p.name), (count.get(first(p.name)) ?? 0) + 1)
  return new Map(people.map((p) => [p.id, (count.get(first(p.name)) ?? 0) > 1 ? p.name : first(p.name)]))
}

/** "09:30" → 570 ("24:00" → 1440). NaN when malformed. */
export function toMin(hhmm: string): number {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm)
  if (!m) return NaN
  return Number(m[1]) * 60 + Number(m[2])
}

export function fromMin(min: number): string {
  const h = Math.floor(min / 60)
  const m = min % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export function isOvernight(s: Pick<ShiftRow, 'start' | 'end'>): boolean {
  return toMin(s.end) <= toMin(s.start)
}

export function shiftText(s: Pick<ShiftRow, 'start' | 'end'>): string {
  return `${s.start}–${s.end}`
}

/** Start times every 30 minutes; ends also offer 24:00 (midnight). */
export function timeOptions(kind: 'start' | 'end'): string[] {
  const out: string[] = []
  for (let m = kind === 'start' ? 0 : 30; m <= (kind === 'start' ? DAY_MIN - 30 : DAY_MIN); m += 30) out.push(fromMin(m))
  return out
}

/** Bangkok wall clock of an instant: date "YYYY-MM-DD", weekday 0 = Monday, minute of the day. */
export function bangkokParts(ms: number): { date: string; weekday: number; minute: number } {
  const d = new Date(ms + BKK_OFFSET_MS)
  const date = d.toISOString().slice(0, 10)
  const weekday = (d.getUTCDay() + 6) % 7
  return { date, weekday, minute: d.getUTCHours() * 60 + d.getUTCMinutes() }
}

export function addDays(date: string, n: number): string {
  const t = Date.parse(`${date}T00:00:00Z`) + n * 86400000
  return new Date(t).toISOString().slice(0, 10)
}

/** "2026-09-28" → "จ. 28 ก.ย." */
export function thaiDay(date: string): string {
  const t = Date.parse(`${date}T00:00:00Z`)
  const d = new Date(t)
  return `${WEEKDAYS_SHORT[(d.getUTCDay() + 6) % 7]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
}

/** The shift a person is in at this Bangkok moment (same rule as api/app/routing/shifts.py). */
export function activeShift(
  shifts: ShiftRow[],
  leaveDays: Set<string>,
  at: { date: string; weekday: number; minute: number },
): ShiftRow | null {
  const yesterday = addDays(at.date, -1)
  for (const s of shifts) {
    const start = toMin(s.start)
    const end = toMin(s.end)
    const over = end <= start
    if (s.weekday === at.weekday && !leaveDays.has(at.date)) {
      if ((over && at.minute >= start) || (!over && at.minute >= start && at.minute < end)) return s
    }
    if (over && s.weekday === (at.weekday + 6) % 7 && !leaveDays.has(yesterday) && at.minute < end) return s
  }
  return null
}

/** Stepper text for minutes: "30 นาที" · "1 ชม." · "1 ชม. 30 น." */
export function minutesText(min: number): string {
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h && m) return `${h} ชม. ${m} น.`
  if (h) return `${h} ชม.`
  return `${m} นาที`
}

/** พร้อมรับ auto-off stepper (0 = ไม่ปิดเอง, else 5–120 minutes; api/app/routing/settings.py AUTO_OFF_RANGE). */
export const AUTO_OFF_STEPS = [0, 5, 10, 15, 20, 30, 45, 60, 90, 120] as const

export function autoOffStep(value: number, dir: 1 | -1): number {
  if (dir > 0) return AUTO_OFF_STEPS.find((s) => s > value) ?? 120
  return [...AUTO_OFF_STEPS].reverse().find((s) => s < value) ?? 0
}

export function autoOffLabel(min: number): string {
  return min === 0 ? 'ไม่ปิดเอง' : minutesText(min)
}

/** Auto-close stepper: 5-minute steps up to an hour, then 15, 30, 60. */
export function closeStep(value: number, dir: 1 | -1): number {
  const step = (v: number) => (v < 60 ? 5 : v < 180 ? 15 : v < 480 ? 30 : 60)
  const s = dir > 0 ? step(value) : step(value - 1)
  return Math.min(1440, Math.max(5, value + dir * s))
}

/** Which settings differ (dict values compared by content). */
export function changedKeys(saved: RoutingValues, draft: RoutingValues): (keyof RoutingValues)[] {
  return (Object.keys(draft) as (keyof RoutingValues)[]).filter((k) => !sameValue(saved[k], draft[k]))
}

export function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(canon(a)) === JSON.stringify(canon(b))
}

function canon(v: unknown): unknown {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return Object.fromEntries(
      Object.entries(v as Record<string, unknown>)
        .filter(([, x]) => x !== null && x !== undefined)
        .sort(([a], [b]) => a.localeCompare(b)),
    )
  }
  return v
}

export function shiftKey(s: ShiftRow): string {
  return `${s.staff_id}|${s.weekday}|${s.start}|${s.end}`
}

export function sameWeek(a: ShiftRow[], b: ShiftRow[]): boolean {
  return a.map(shiftKey).sort().join() === b.map(shiftKey).sort().join()
}

export function sameLeave(a: LeaveRow[], b: LeaveRow[]): boolean {
  const k = (l: LeaveRow) => `${l.staff_id}|${l.day}|${l.note}`
  return a.map(k).sort().join() === b.map(k).sort().join()
}

/** The most used shift times, for quick-pick chips in the editor. */
export function commonShifts(shifts: ShiftRow[], n = 3): { start: string; end: string }[] {
  const count = new Map<string, number>()
  for (const s of shifts) count.set(`${s.start}|${s.end}`, (count.get(`${s.start}|${s.end}`) ?? 0) + 1)
  const top = [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, n)
  const picks = top.map(([k]) => {
    const [start, end] = k.split('|') as [string, string]
    return { start, end }
  })
  for (const d of [
    { start: '09:00', end: '18:00' },
    { start: '13:00', end: '22:00' },
  ]) {
    if (picks.length < n && !picks.some((p) => p.start === d.start && p.end === d.end)) picks.push(d)
  }
  return picks
}

/** One timeline line (SystemLine.vue) as text parts: `lead` + bold `staff` + `tail`. */
const FOLLOW_UP_LEAD: Record<string, string> = {
  sent: 'ทักตามแล้ว โดย',
  skipped: 'ข้ามการทักตาม โดย',
  auto: 'ระบบทักตามอัตโนมัติ',
}

/** "AI หยุดดูแล" line: the reason is a code from api/app/ai/aimode.py (end_reason). */
const AI_OFF_LEAD: Record<string, string> = {
  turned_off: 'หยุด AI',
  staff_took_over: 'AI หยุด เพราะพนักงานตอบเอง',
  order_done: 'AI ลงคำสั่งซื้อแล้ว หยุดดูแล',
  closed: 'AI หยุด เพราะปิดแชท',
  error: 'AI หยุด เพราะขัดข้อง',
}
function aiOffParts(reason: string | null, staff: string | null): { lead: string; staff: string | null; tail: string } {
  const lead = AI_OFF_LEAD[reason ?? ''] ?? 'AI หยุดดูแลแชทนี้'
  if (reason === 'turned_off') return { lead: staff ? 'หยุด AI โดย' : 'AI หยุดดูแลแชทนี้', staff, tail: '' }
  if (reason === 'staff_took_over' && staff) return { lead: 'AI หยุด เพราะพนักงานตอบเอง ·', staff, tail: '' }
  return { lead, staff: null, tail: '' }
}

export function systemLineParts(item: Pick<SystemEvent, 'event' | 'staff' | 'reason'>): {
  lead: string
  staff: string | null
  tail: string
} {
  const reason = item.reason?.trim() || null
  const staff = item.staff?.name ?? null
  switch (item.event) {
    case 'ai_handoff':
      return { lead: 'AI ส่งต่อให้', staff, tail: reason ? ` เพราะ${reason}` : '' }
    case 'ai_on':
      // AI mode (docs/ai-mode.md): staff = who pressed "ให้ AI ดูแล"
      return { lead: staff ? 'ให้ AI ดูแลแชทนี้ โดย' : 'ให้ AI ดูแลแชทนี้', staff, tail: '' }
    case 'ai_off':
      return aiOffParts(reason, staff)
    case 'assigned': {
      const auto = reason?.startsWith('แจกอัตโนมัติ')
      if (auto) {
        const why = reason!.replace(/^แจกอัตโนมัติ\s*·?\s*/, '')
        return { lead: 'แจกอัตโนมัติให้', staff, tail: why ? ` · ${why}` : '' }
      }
      return { lead: 'ย้ายแชทให้', staff, tail: reason ? ` · ${reason}` : '' }
    }
    case 'closed':
      if (reason) return { lead: reason, staff: null, tail: '' }
      return { lead: staff ? 'ปิดแชทโดย' : 'ปิดแชท', staff, tail: '' }
    case 'reopened':
      if (reason) return { lead: 'เปิดแชทอีกครั้ง', staff: null, tail: ` · ${reason}` }
      return { lead: staff ? 'เปิดแชทอีกครั้งโดย' : 'เปิดแชทอีกครั้ง', staff, tail: '' }
    case 'requeued':
      // api/app/routing/engine.py requeue_off_shift: staff = the previous owner, reason หมดกะ / ลาวันนี้
      if (reason === 'ลาวันนี้') return { lead: 'ย้ายเข้าคิวกลาง ·', staff, tail: ' ลาวันนี้' }
      return { lead: staff ? 'ย้ายเข้าคิวกลาง · หมดกะของ' : 'ย้ายเข้าคิวกลาง · หมดกะ', staff, tail: '' }
    case 'follow_up':
      // api/app/collab/followup.py: the reason is a code, shown as the lead text
      return { lead: FOLLOW_UP_LEAD[reason ?? ''] ?? 'ทักตามแล้ว', staff, tail: '' }
  }
  return { lead: '', staff, tail: reason ? ` · ${reason}` : '' }
}
